import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Phone, Clock, DollarSign, User, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function ExecutionDetail() {
  const { executionId } = useParams();
  const navigate = useNavigate();
  const [execution, setExecution] = useState(null);
  const [loading, setLoading] = useState(true);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => {
    const fetchExecution = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${BACKEND_URL}/api/executions/${executionId}`, getAuthHeaders());
        setExecution(response.data);
      } catch (error) {
        toast.error('Failed to fetch execution details');
      } finally {
        setLoading(false);
      }
    };

    fetchExecution();
  }, [executionId]);

  if (loading) {
    return <div className="flex items-center justify-center h-96" data-testid="loading-detail">Loading execution details...</div>;
  }

  if (!execution) {
    return <div className="flex items-center justify-center h-96" data-testid="not-found">Execution not found</div>;
  }

  const getStatusBadge = (status) => {
    const variants = {
      completed: 'default',
      failed: 'destructive',
      in_progress: 'secondary',
      dropped: 'destructive',
      rescheduled: 'outline',
      not_started: 'secondary'
    };
    return <Badge variant={variants[status] || 'outline'}>{status}</Badge>;
  };

  // Parse transcript into conversation bubbles
  const parseTranscript = (transcript) => {
    if (!transcript) return [];
    
    const lines = transcript.split('\n').filter(line => line.trim());
    const messages = [];
    
    for (const line of lines) {
      const match = line.match(/^(assistant|user|agent):\s*(.+)$/i);
      if (match) {
        messages.push({
          role: match[1].toLowerCase(),
          content: match[2].trim()
        });
      }
    }
    
    return messages;
  };

  const transcriptMessages = parseTranscript(execution.transcript);

  return (
    <div className="space-y-6" data-testid="execution-detail-container">
      <div className="flex items-center gap-4">
        <Button variant="outline" onClick={() => navigate('/executions')} data-testid="back-button">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <h1 className="text-3xl font-bold">Call Transcript</h1>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card data-testid="detail-status-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Status</CardTitle>
            <Phone className="h-4 w-4 text-slate-500" />
          </CardHeader>
          <CardContent>
            {getStatusBadge(execution.status)}
          </CardContent>
        </Card>

        <Card data-testid="detail-duration-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Duration</CardTitle>
            <Clock className="h-4 w-4 text-slate-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{Math.round(execution.duration_seconds)}s</div>
          </CardContent>
        </Card>

        <Card data-testid="detail-cost-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Cost</CardTitle>
            <DollarSign className="h-4 w-4 text-slate-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${execution.cost_usd?.toFixed(4)}</div>
          </CardContent>
        </Card>

        <Card data-testid="detail-user-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">User</CardTitle>
            <User className="h-4 w-4 text-slate-500" />
          </CardHeader>
          <CardContent>
            <div className="text-sm font-medium truncate">{execution.user_number || 'N/A'}</div>
          </CardContent>
        </Card>
      </div>

      {/* Transcript Section */}
      <Card data-testid="transcript-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Conversation Transcript
          </CardTitle>
          <p className="text-sm text-slate-500">
            {new Date(execution.started_at).toLocaleString()} • {transcriptMessages.length} messages
          </p>
        </CardHeader>
        <CardContent>
          {transcriptMessages.length > 0 ? (
            <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
              {transcriptMessages.map((message, index) => (
                <div
                  key={index}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  data-testid={`message-${index}`}
                >
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                      message.role === 'user'
                        ? 'bg-blue-600 text-white rounded-br-md'
                        : 'bg-slate-100 text-slate-900 rounded-bl-md'
                    }`}
                  >
                    <div className="text-xs font-medium mb-1 opacity-70">
                      {message.role === 'user' ? 'Customer' : 'Agent'}
                    </div>
                    <p className="text-sm leading-relaxed">{message.content}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-slate-500">
              <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <p>No transcript available for this call</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recording Section (if available) */}
      {execution.recording_url && (
        <Card data-testid="recording-card">
          <CardHeader>
            <CardTitle>Call Recording</CardTitle>
          </CardHeader>
          <CardContent>
            <audio controls className="w-full" data-testid="audio-player">
              <source src={execution.recording_url} type="audio/mpeg" />
              Your browser does not support the audio element.
            </audio>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default ExecutionDetail;
